// @flow

import React, { type Element, useEffect, useState } from 'react'
import { Box, Container, Grid, Typography } from '@material-ui/core'
import { MuiThemeProvider } from '@material-ui/core/styles'
import theme from '../../../theme'
import AlertError from '../../Common/AlertError'
import type {
  Facility,
  PrivateFacility,
  RuralLocation,
  ServiceLocation,
  Speciality,
  Diagnosis,
} from '../types'
import SpecialityContainer from '../SpecialityContainer'
import DefaultRuralLocationContainer from '../DefaultRuralLocationContainer'
import DefaultServiceLocationContainer from '../DefaultServiceLocationContainer'
import DefaultDiagnosesContainer from '../DefaultDiagnosesContainer'
import DefaultFacilityContainer from '../DefaultFacilityContainer'
import BcpContainer from '../BcpContainer'
import { UserContext, UserContextProps } from '../../Common/UserContext'
import { request } from '../../Common/Request'
import AlertSuccess from '../../Common/AlertSuccess'
import { grey } from '@material-ui/core/colors'

type Props = {|
  userId: string,
  role: string,
  isBcpEnrolled: boolean,
  createdFacilities: Array<PrivateFacility>,
  serverErrors?: { [string]: Array<string> },
  currentSpeciality: Speciality,
  specialityList: Array<Speciality>,
  currentGimTraining: boolean,
  currentRuralLocation: RuralLocation,
  ruralLocationList: Array<RuralLocation>,
  currentServiceLocation: ServiceLocation,
  serviceLocationList: Array<ServiceLocation>,
  currentDiagnoses: Array<Diagnosis>,
  diagnosesList: Array<Diagnosis>,
  currentDefaultFacility: Facility,
  facilityList: Array<Facility>,
  bcpEnabled: boolean,
|}

function AccountSettingsContainer({
  userId,
  role,
  isBcpEnrolled,
  createdFacilities,
  serverErrors,
  currentSpeciality,
  specialityList,
  currentGimTraining,
  currentRuralLocation,
  ruralLocationList,
  currentServiceLocation,
  serviceLocationList,
  currentDiagnoses,
  diagnosesList,
  currentDefaultFacility,
  facilityList,
  bcpEnabled,
}: Props): Element<typeof MuiThemeProvider> {
  const [allFacilities, setAllFacilities] = useState(facilityList ?? [])
  const [facilitiesUpdated, setFacilitiesUpdated] = useState(false)
  const [facilityToAdd, setFacilityToAdd] = useState(allFacilities[0] ?? {})
  const [facilityToDelete, setFacilityToDelete] = useState('')
  const [errorObject, setErrorObject] = useState(serverErrors ?? {})
  const [nonFieldErrors, setNonFieldErrors] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const userContextProps = new UserContextProps(userId, role)

  function handleServerErrors(errors: { [string]: Array<string> }): void {
    setErrorObject(errors)
    if (errors.non_field_errors) setNonFieldErrors({ non_field_errors: errors.non_field_errors })
  }

  function handleSuccess(): void {
    setSuccessMessage('Profile updated successfully')
  }

  async function updateProfile(payload): Promise<boolean> {
    const path = userContextProps.updateProfilePath()

    return await request(
      path,
      'PATCH',
      {},
      JSON.stringify(payload),
      handleServerErrors,
      handleSuccess
    )
  }

  useEffect(() => {
    setAllFacilities((prevState) => {
      if (!prevState || !prevState.length) return prevState

      const facilityExists = prevState.some((facility) => {
        return facility.id === facilityToAdd.id
      })
      if (facilityExists) return prevState

      return prevState.concat([facilityToAdd])
    })
  }, [facilityToAdd])

  useEffect(() => {
    setAllFacilities((prevState) => {
      if (!prevState || !prevState.length || !facilityToDelete) return prevState

      const indexToRemove = prevState.findIndex((facility) => {
        return facility.id === facilityToDelete
      })

      const newState = prevState
      newState.splice(indexToRemove, 1)
      return newState
    })
    setFacilitiesUpdated((prev) => !prev)
  }, [facilityToDelete])

  return (
    <MuiThemeProvider theme={theme}>
      <Grid item xs={12}>
        {nonFieldErrors && <AlertError errors={nonFieldErrors} />}
        {successMessage && <AlertSuccess message={successMessage} setMessage={setSuccessMessage} />}
      </Grid>
      <Container maxWidth="md" disableGutters>
        <Box py={2} fontWeight="fontWeightMedium" color={grey[900]}>
          <Typography variant="h4" color="inherit">
            Profile
          </Typography>
        </Box>
        <Box width={1} pb={2}>
          <SpecialityContainer
            currentSpeciality={currentSpeciality}
            specialityList={specialityList}
            currentGimTraining={currentGimTraining}
            updateProfile={updateProfile}
            error={errorObject.speciality_id ?? []}
          />
          <DefaultRuralLocationContainer
            currentRuralLocation={currentRuralLocation}
            ruralLocationList={ruralLocationList}
            updateProfile={updateProfile}
            error={errorObject.default_rural ?? []}
          />
          <DefaultServiceLocationContainer
            currentServiceLocation={currentServiceLocation}
            serviceLocationList={serviceLocationList}
            updateProfile={updateProfile}
            error={errorObject.default_service_location ?? []}
          />
          <DefaultDiagnosesContainer
            currentDiagnoses={currentDiagnoses}
            diagnosesList={diagnosesList}
            updateProfile={updateProfile}
            error={errorObject.default_diagnosis_ids ?? []}
          />
          <DefaultFacilityContainer
            currentDefaultFacility={currentDefaultFacility}
            facilityList={allFacilities}
            handleServerErrors={handleServerErrors}
            updateProfile={updateProfile}
            facilitiesUpdated={facilitiesUpdated}
            error={errorObject.default_facility_code ?? []}
          />
        </Box>
      </Container>
      {bcpEnabled && (
        <UserContext.Provider value={userContextProps}>
          <BcpContainer
            isBcpEnrolled={isBcpEnrolled}
            handleServerErrors={setErrorObject}
            createdFacilities={createdFacilities}
            setFacilityToAdd={setFacilityToAdd}
            setFacilityToDelete={setFacilityToDelete}
          />
        </UserContext.Provider>
      )}
    </MuiThemeProvider>
  )
}

export default AccountSettingsContainer
